// Diagram renderers. Both lazy-load their libraries (mermaid, cytoscape) via
// dynamic import so they don't bloat the initial bundle — keep it that way.

import { useEffect, useRef, useState } from "react";

import type { Artifact } from "@/types/eryndal";
import { ArtifactFrame } from "./common";

// Mermaid's flowchart parser chokes when a node label contains parens,
// braces, or other shape-delimiter characters that aren't quoted. The
// model often emits things like NIS2_24[NIS2 Art.23(4)(a) early warning]
// where (4)(a) confuses the lexer. Wrap label text in double quotes when
// such characters are present and the label isn't already quoted.
function sanitiseMermaid(source: string): string {
  const fix = (open: string, close: string) => {
    const re = new RegExp(`\\${open}([^\\${open}\\${close}"\\n][^\\${close}\\n"]*)\\${close}`, "g");
    return (s: string) =>
      s.replace(re, (match, inner: string) => {
        if (/[(){}<>:;]/.test(inner) && !/^".*"$/.test(inner.trim())) {
          return `${open}"${inner.replace(/"/g, "&quot;")}"${close}`;
        }
        return match;
      });
  };
  return [fix("[", "]"), fix("(", ")"), fix("{", "}")].reduce((acc, f) => f(acc), source);
}

// Mermaid's themeVariables take literal colours, not Tailwind classes, so the
// atelier palette has to be restated here. Keep in step with the token
// definitions in frontend/tailwind.config.js if those ever move.
const MERMAID_TOKENS = {
  parchment: "#f7f3ea", // parchment-100
  surface: "#fdfbf6", // parchment-50
  ink: "#1a1612", // espresso-700
  rule: "#d8cfb9", // ruleline
  alarmWash: "#fbeeec", // oxblood-50
  oxblood300: "#c66a5c", // oxblood-300
  warmgray300: "#a89878", // warmgray-300
} as const;

export function MermaidArtifact({ artifact }: { artifact: Artifact }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const node = ref.current;
    if (!node) return;
    (async () => {
      try {
        const mod = await import("mermaid");
        const mermaid = mod.default;
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: "strict",
          // theme "base" is the only one that honours themeVariables. The
          // previous "neutral" theme rendered grey-on-white boxes that read as
          // a default Mermaid diagram dropped into the page; these values map
          // the atelier tokens so diagrams sit in the same world as the rest
          // of the answer.
          theme: "base",
          themeVariables: {
            fontFamily: "Inter, system-ui, sans-serif",
            fontSize: "13px",
            // Nodes: surface fill, oxblood-300 edge, espresso text.
            primaryColor: MERMAID_TOKENS.surface,
            primaryBorderColor: MERMAID_TOKENS.oxblood300,
            primaryTextColor: MERMAID_TOKENS.ink,
            secondaryColor: MERMAID_TOKENS.alarmWash,
            secondaryBorderColor: MERMAID_TOKENS.oxblood300,
            secondaryTextColor: MERMAID_TOKENS.ink,
            tertiaryColor: MERMAID_TOKENS.parchment,
            tertiaryBorderColor: MERMAID_TOKENS.rule,
            tertiaryTextColor: MERMAID_TOKENS.ink,
            // Connectors and labels.
            lineColor: MERMAID_TOKENS.warmgray300,
            textColor: MERMAID_TOKENS.ink,
            mainBkg: MERMAID_TOKENS.surface,
            background: MERMAID_TOKENS.parchment,
            nodeBorder: MERMAID_TOKENS.oxblood300,
            clusterBkg: MERMAID_TOKENS.parchment,
            clusterBorder: MERMAID_TOKENS.rule,
            edgeLabelBackground: MERMAID_TOKENS.parchment,
            titleColor: MERMAID_TOKENS.ink,
          },
        });
        const id = `mermaid-${Math.random().toString(36).slice(2)}`;
        const cleaned = sanitiseMermaid(artifact.payload);
        const { svg } = await mermaid.render(id, cleaned);
        if (!cancelled && node) node.innerHTML = svg;
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to render diagram");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [artifact.payload]);

  return (
    <ArtifactFrame title={artifact.title} kindLabel="mermaid diagram">
      {error ? (
        <pre className="whitespace-pre-wrap font-mono text-xs text-oxblood-500">{error}</pre>
      ) : (
        <div ref={ref} className="overflow-x-auto" />
      )}
    </ArtifactFrame>
  );
}

export function CytoscapeArtifact({ artifact }: { artifact: Artifact }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const node = ref.current;
    if (!node) return;
    (async () => {
      try {
        const cy = (await import("cytoscape")).default;
        const elements = JSON.parse(artifact.payload);
        if (cancelled || !node) return;
        cy({
          container: node,
          elements,
          style: [
            {
              selector: "node",
              style: {
                "background-color": "#8b3a30",
                label: "data(label)",
                color: "#1a1612",
                "font-size": "11px",
                "font-family": "Inter, sans-serif",
                "text-valign": "bottom",
                "text-margin-y": 4,
              },
            },
            {
              selector: "edge",
              style: {
                "line-color": "#a89878",
                "target-arrow-color": "#a89878",
                "target-arrow-shape": "triangle",
                "curve-style": "bezier",
                width: 1.2,
              },
            },
          ],
          layout: { name: "cose", animate: false },
        });
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to render graph");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [artifact.payload]);

  return (
    <ArtifactFrame title={artifact.title} kindLabel="graph">
      {error ? (
        <pre className="whitespace-pre-wrap font-mono text-xs text-oxblood-500">{error}</pre>
      ) : (
        <div ref={ref} className="h-[400px] w-full bg-parchment-100" />
      )}
    </ArtifactFrame>
  );
}
